import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import {
  Form,
  Button,
  Icon,
  Stack,
  Row,
  Col,
  Hyperlink,
} from '@openedx/paragon';
import { useNavigate } from 'react-router-dom';
import { DragIndicator, Close } from '@openedx/paragon/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Course {
  courseId: string;
  displayName: string;
}

interface CourseListPickerProps {
  courses: Course[];
  selectedCourseIds: string[];
  onCourseListChange: (courseIds: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  edxCourseOrg?: string;
  companyFriendlyName?: string;
  companyCatalogIDs?: string[];
}

interface SortableItemProps {
  course: Course;
  onRemove: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ course, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: course.courseId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="d-flex align-items-center border rounded p-2 mb-2 bg-light"
    >
      <div
        {...attributes}
        {...listeners}
        className="d-flex align-items-center cursor-pointer"
        style={{ cursor: 'grab' }}
      >
        <Icon src={DragIndicator} className="text-muted mr-2" />
      </div>
      <div className="flex-grow-1">
        <strong>{course.courseId}</strong> - {course.displayName}
      </div>
      <Button
        variant="link"
        size="sm"
        onClick={onRemove}
        iconBefore={Close}
        className="text-danger"
      />
    </div>
  );
};

const CourseListPicker: React.FC<CourseListPickerProps> = ({
  courses,
  selectedCourseIds,
  onCourseListChange,
  isLoading = false,
  disabled = false,
  edxCourseOrg = '',
  companyFriendlyName = '',
  companyCatalogIDs = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [courseFilter, setCourseFilter] = useState<'company' | 'uopx' | 'both' | 'all'>('company');
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  /**
   * Extract organization code from course ID
   * Format: course-v1:ORG+COURSE+RUN
   */
  const extractOrgFromCourseId = (courseId: string): string => {
    const match = courseId.match(/^course-v1:([^+]+)\+/);
    return match ? match[1] : '';
  };

  /**
   * Courses scoped to the catalog — this is the base list for all filtering and search.
   * Display names are resolved from the full course list passed in via the `courses` prop.
   * If no catalog is loaded (companyCatalogIDs is empty), this will be empty.
   */
  const catalogCourses = useMemo(
    () => courses.filter((c) => companyCatalogIDs.includes(c.courseId)),
    [courses, companyCatalogIDs],
  );

  /**
   * Filter catalog courses based on the selected org radio filter.
   * Operates on catalogCourses (not the full course list) so filters
   * always stay within the catalog.
   */
  const filterCoursesByOrg = (courseList: Course[]) => {
    if (courseFilter === 'all') {
      return courseList;
    }

    return courseList.filter((course) => {
      const courseOrg = extractOrgFromCourseId(course.courseId).toLowerCase();
      const edxCourseOrgLower = edxCourseOrg.toLowerCase();

      if (courseFilter === 'company') {
        return courseOrg === edxCourseOrgLower;
      } if (courseFilter === 'uopx') {
        return courseOrg === 'uopx';
      } if (courseFilter === 'both') {
        return courseOrg === edxCourseOrgLower || courseOrg === 'uopx';
      }

      return true;
    });
  };

  /**
   * Count of catalog courses visible under the current org filter —
   * used for the static hint below the search box.
   */
  const visibleCatalogCount = useMemo(
    () => filterCoursesByOrg(catalogCourses).length,
    [catalogCourses, courseFilter, edxCourseOrg],
  );

  // Debounce the search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Scroll highlighted item into view when navigating with keyboard
  useEffect(() => {
    if (showDropdown && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex, showDropdown]);

  // Filter catalog courses based on debounced search query and org filter.
  // Base list is catalogCourses (already scoped to the catalog), so no additional
  // catalog ID check is needed here.
  const filteredCourses = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return [];
    }

    const query = debouncedSearchQuery.toLowerCase();
    const orgFiltered = filterCoursesByOrg(catalogCourses);

    return orgFiltered.filter(
      (course) => !selectedCourseIds.includes(course.courseId)
        && (course.courseId.toLowerCase().includes(query)
          || course.displayName.toLowerCase().includes(query)),
    );
  }, [catalogCourses, debouncedSearchQuery, selectedCourseIds, courseFilter, edxCourseOrg]);

  // Get selected courses with their display names
  const selectedCourses = useMemo(() => (
    selectedCourseIds
      .map((id) => courses.find((c) => c.courseId === id))
      .filter((c): c is Course => c !== undefined)
  ), [selectedCourseIds, courses]);

  // Setup DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedCourseIds.indexOf(active.id as string);
      const newIndex = selectedCourseIds.indexOf(over.id as string);

      const newOrder = arrayMove(selectedCourseIds, oldIndex, newIndex);
      onCourseListChange(newOrder);
    }
  };

  const handleSelectCourse = (course: Course, keepOpen = false) => {
    if (!selectedCourseIds.includes(course.courseId)) {
      onCourseListChange([...selectedCourseIds, course.courseId]);
    }
    // Close dropdown and clear search unless Ctrl/Cmd was held
    if (!keepOpen) {
      setShowDropdown(false);
      setSearchQuery('');
      setDebouncedSearchQuery('');
    }
  };

  const handleRemoveCourse = (courseId: string) => {
    onCourseListChange(selectedCourseIds.filter((id) => id !== courseId));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowDropdown(value.trim().length > 0);
    setHighlightedIndex(0); // Reset highlight when search changes
  };

  const handleClear = () => {
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || filteredCourses.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (
          prev < filteredCourses.length - 1 ? prev + 1 : prev
        ));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCourses[highlightedIndex]) {
          handleSelectCourse(filteredCourses[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  /**
   * Render the static catalog hint shown below the search box before the user types.
   * Covers three states: no org selected, org selected with empty catalog, org with courses.
   */
  const renderCatalogHint = () => {
    if (isLoading) {
      return null; // Loading spinner is shown separately
    }

    if (!companyFriendlyName) {
      return (
        <Form.Text className="text-muted">
          Select an organization to see available courses.
        </Form.Text>
      );
    }

    if (companyCatalogIDs.length === 0) {
      return (
        <Form.Text className="text-danger">
          No courses found in <strong>{companyFriendlyName}</strong>&apos;s catalog. Add courses via{' '}
          <Hyperlink
            destination="/authoring/company-management-portal/"
            onClick={(e) => { e.preventDefault(); navigate('/company-management-portal/'); }}
            showLaunchIcon
          >
            CMP
          </Hyperlink>
          {' '}→ License Management.
        </Form.Text>
      );
    }

    return (
      <Form.Text className="text-muted">
        {visibleCatalogCount === 0
          ? `No courses match the current filter in ${companyFriendlyName}'s catalog.`
          : `${visibleCatalogCount} course${visibleCatalogCount !== 1 ? 's' : ''} available in ${companyFriendlyName}'s catalog with current filter.`}
      </Form.Text>
    );
  };

  /**
   * Render the dropdown empty state message based on context.
   */
  const renderEmptyDropdown = () => {
    if (!companyFriendlyName) {
      return (
        <div
          className="position-absolute w-100 border rounded bg-white shadow-sm p-2 text-muted"
          style={{ zIndex: 1000, top: '100%', marginTop: '4px' }}
        >
          Select an organization to see available courses.
        </div>
      );
    }

    if (companyCatalogIDs.length === 0) {
      return (
        <div
          className="position-absolute w-100 border rounded bg-white shadow-sm p-2 text-muted"
          style={{ zIndex: 1000, top: '100%', marginTop: '4px' }}
        >
          No courses in <strong>{companyFriendlyName}</strong>&apos;s catalog. Add courses via{' '}
          <Hyperlink
            destination="/authoring/company-management-portal/"
            onClick={(e) => { e.preventDefault(); navigate('/company-management-portal/'); }}
            showLaunchIcon
          >
            CMP
          </Hyperlink>
          {' '}→ License Management.
        </div>
      );
    }

    return (
      <div
        className="position-absolute w-100 border rounded bg-white shadow-sm p-2 text-muted"
        style={{ zIndex: 1000, top: '100%', marginTop: '4px' }}
      >
        No courses matching &ldquo;{debouncedSearchQuery}&rdquo; found in{' '}
        <strong>{companyFriendlyName}</strong>&apos;s catalog.
      </div>
    );
  };

  return (
    <div className="course-list-picker">
      {/* Course Filter Options */}
      <Form.Group className="mb-3">
        <Form.Label>Filter Courses (Courses Must Be In Catalog)</Form.Label>
        <Form.Text>
          If courses are not present in dropdown navigate to{' '}
          <Hyperlink
            destination="/authoring/company-management-portal/"
            onClick={(e) => { e.preventDefault(); navigate('/company-management-portal/'); }}
            showLaunchIcon
          >
            CMP
          </Hyperlink>
          {' '}and add the course to the catalog via License Management
        </Form.Text>
        <Row>
          <Col xs={6}>
            <Form.Check
              type="radio"
              id="filter-all"
              name="courseFilter"
              label="Show all catalog courses"
              checked={courseFilter === 'all'}
              onChange={() => setCourseFilter('all')}
            />
          </Col>
          <Col xs={6}>
            <Form.Check
              type="radio"
              id="filter-company"
              name="courseFilter"
              label={`Show only ${companyFriendlyName || 'organization'} courses`}
              checked={courseFilter === 'company'}
              onChange={() => setCourseFilter('company')}
              disabled={!edxCourseOrg}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <Form.Check
              type="radio"
              id="filter-uopx"
              name="courseFilter"
              label="Show only UOPX courses"
              checked={courseFilter === 'uopx'}
              onChange={() => setCourseFilter('uopx')}
            />
          </Col>
          <Col xs={6}>
            <Form.Check
              type="radio"
              id="filter-both"
              name="courseFilter"
              label={`Show ${companyFriendlyName || 'organization'} and UOPX courses`}
              checked={courseFilter === 'both'}
              onChange={() => setCourseFilter('both')}
              disabled={!edxCourseOrg}
            />
          </Col>
        </Row>
      </Form.Group>

      <Form.Group>
        <Form.Label>Course List</Form.Label>
        <div className="position-relative">
          <div className="position-relative">
            <Form.Control
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by course ID or name..."
              disabled={disabled || isLoading || companyCatalogIDs.length === 0}
            />
            {searchQuery && (
              <Button
                variant="link"
                size="sm"
                onClick={handleClear}
                iconBefore={Close}
                className="position-absolute"
                style={{
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '4px',
                  zIndex: 10,
                }}
                aria-label="Clear search"
              />
            )}
          </div>
          {showDropdown && filteredCourses.length > 0 && (
            <div
              ref={dropdownRef}
              className="position-absolute w-100 border rounded bg-white shadow-sm"
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1000,
                top: '100%',
                marginTop: '4px',
              }}
            >
              {filteredCourses.map((course, index) => (
                <div
                  key={course.courseId}
                  ref={(el) => { itemRefs.current[index] = el; }}
                  className="p-2 cursor-pointer"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: index === highlightedIndex ? '#e7f3ff' : 'transparent',
                  }}
                  onClick={(e) => handleSelectCourse(course, e.ctrlKey || e.metaKey)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectCourse(course, e.ctrlKey || e.metaKey);
                    }
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="button"
                  tabIndex={0}
                >
                  <strong>{course.courseId}</strong> - {course.displayName}
                </div>
              ))}
            </div>
          )}
          {showDropdown && filteredCourses.length === 0 && debouncedSearchQuery && (
            renderEmptyDropdown()
          )}
        </div>

        {/* Static catalog hint — always visible below the search box */}
        {renderCatalogHint()}

        <Form.Text className="text-muted mt-1">
          Search by course ID or name. <strong>Tip:</strong> Ctrl+click (Cmd+click on Mac) selects multiple courses.
        </Form.Text>
      </Form.Group>

      {selectedCourses.length > 0 && (
        <div className="mt-3">
          <Form.Label>Selected Courses (drag to reorder)</Form.Label>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedCourseIds}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap={2}>
                {selectedCourses.map((course) => (
                  <SortableItem
                    key={course.courseId}
                    course={course}
                    onRemove={() => handleRemoveCourse(course.courseId)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {isLoading && (
        <div className="text-muted mt-2">Loading courses...</div>
      )}
    </div>
  );
};

export default CourseListPicker;
